import React, { useEffect } from 'react';
import {
  Trophy,
  Crown,
  Sparkles,
  Award,
  RotateCcw,
  BookOpen,
  ArrowRight,
  Flame,
  CheckCircle2
} from 'lucide-react';
import confetti from 'canvas-confetti';
import { SessionGameState } from '../../../types/session-games';

interface GameOverLeaderboardProps {
  game: SessionGameState;
  currentUserId: string;
  onRematch: () => void;
  onClose: () => void;
}

export const GameOverLeaderboard: React.FC<GameOverLeaderboardProps> = ({
  game,
  currentUserId,
  onRematch,
  onClose
}) => {
  useEffect(() => {
    // Launch celebratory confetti
    confetti({
      particleCount: 100,
      spread: 70,
      origin: { y: 0.6 }
    });
  }, []);

  const playersArr = Object.values(game.players || {}).sort((a, b) => b.score - a.score);
  const isWinner = game.winnerId === currentUserId;
  const isTie = game.winnerId === 'tie';

  return (
    <div className="flex flex-col items-center justify-center p-6 md:p-8 max-w-lg mx-auto text-center space-y-6">
      {/* Trophy & Header */}
      <div className="relative">
        <div className="w-24 h-24 rounded-3xl bg-gradient-to-tr from-amber-400 via-yellow-500 to-orange-500 text-white flex items-center justify-center shadow-xl mx-auto transform -rotate-3 hover:rotate-0 transition-transform">
          <Trophy className="w-12 h-12" />
        </div>
        <div className="absolute -top-2 -right-2 bg-purple-600 text-white p-2 rounded-full shadow-md animate-bounce">
          <Sparkles className="w-4 h-4" />
        </div>
      </div>

      <div>
        <h2 className="text-2xl md:text-3xl font-black text-gray-900 tracking-tight">
          {isTie
            ? "It's an Epic Tie! 🤝"
            : isWinner
            ? 'Victory! You Won! 🏆'
            : 'Well Played! Great Match! 👏'}
        </h2>
        <p className="text-xs md:text-sm text-gray-500 font-medium mt-1">
          Game Completed • {game.totalRounds} Rounds Played Together
        </p>
      </div>

      {/* Players Podium / Score List */}
      <div className="w-full bg-white rounded-3xl p-5 border border-gray-100 shadow-md space-y-3">
        {playersArr.map((player, idx) => {
          const isTop = idx === 0 && !isTie;
          const isMe = player.userId === currentUserId;

          return (
            <div
              key={player.userId}
              className={`p-3.5 rounded-2xl border flex items-center justify-between transition-all ${
                isTop
                  ? 'bg-amber-50/60 border-amber-300 shadow-sm'
                  : 'bg-gray-50/60 border-gray-200'
              }`}
            >
              <div className="flex items-center gap-3">
                <div className="relative">
                  {player.userPhoto ? (
                    <img
                      src={player.userPhoto}
                      alt={player.userName}
                      className="w-10 h-10 rounded-full object-cover border-2 border-white shadow-xs"
                    />
                  ) : (
                    <div className="w-10 h-10 rounded-full bg-gradient-to-tr from-purple-500 to-indigo-600 text-white font-bold flex items-center justify-center text-sm shadow-xs">
                      {player.userName.charAt(0).toUpperCase()}
                    </div>
                  )}
                  {isTop && (
                    <span className="absolute -top-2 -right-1 bg-amber-500 text-white p-0.5 rounded-full shadow-xs">
                      <Crown className="w-3.5 h-3.5" />
                    </span>
                  )}
                </div>

                <div className="text-left">
                  <p className="font-bold text-gray-900 text-sm leading-tight flex items-center gap-1.5">
                    <span>{player.userName}</span>
                    {isMe && (
                      <span className="text-[10px] font-black uppercase tracking-wider text-purple-600 bg-purple-100 px-1.5 py-0.2 rounded">
                        You
                      </span>
                    )}
                  </p>
                  <p className="text-[11px] text-gray-400 mt-0.5">
                    {Math.max(30, player.score * 2)} Leaderboard XP Awarded
                  </p>
                </div>
              </div>

              <div className="text-right">
                <span className="text-xl font-black text-gray-900">{player.score}</span>
                <span className="text-xs font-semibold text-gray-400 ml-1">pts</span>
              </div>
            </div>
          );
        })}
      </div>

      {/* Action Buttons */}
      <div className="flex flex-col sm:flex-row items-center gap-3 w-full">
        <button
          onClick={onRematch}
          className="w-full sm:flex-1 py-3 px-5 rounded-2xl bg-gradient-to-r from-purple-600 to-indigo-600 text-white font-bold text-sm shadow-md hover:opacity-95 transition-all flex items-center justify-center gap-2"
        >
          <RotateCcw className="w-4 h-4" />
          <span>Play Again</span>
        </button>

        <button
          onClick={onClose}
          className="w-full sm:flex-1 py-3 px-5 rounded-2xl border border-gray-200 bg-white hover:bg-gray-50 text-gray-700 font-bold text-sm transition-all flex items-center justify-center gap-2"
        >
          <BookOpen className="w-4 h-4" />
          <span>Back to Room</span>
        </button>
      </div>
    </div>
  );
};
